<template>
  <v-dialog :value="value" max-width="980" @input="$emit('input', $event)">
    <div class="seg">
      <div class="seg__head">
        <div>
          <div class="seg__eyebrow">{{ title }}</div>
          <h3 class="seg__title">Demanda del segmento</h3>
        </div>
        <button type="button" class="seg__close" aria-label="Cerrar" @click="$emit('input', false)">×</button>
      </div>

      <div v-if="loading" class="seg__loading">Cargando…</div>

      <template v-else-if="seg">
        <!-- KPIs -->
        <div class="seg__kpis">
          <div class="seg__kpi"><div class="seg__kpinum">{{ seg.totalVisits }}</div><div class="seg__kpilbl">Vistas</div></div>
          <div class="seg__kpi"><div class="seg__kpinum">{{ seg.uniqueVisitors }}</div><div class="seg__kpilbl">Visitantes únicos</div></div>
          <div class="seg__kpi"><div class="seg__kpinum">{{ seg.recurrentVisitors }}</div><div class="seg__kpilbl">Recurrentes</div></div>
          <div class="seg__kpi"><div class="seg__kpinum">{{ seg.inquiries }}</div><div class="seg__kpilbl">Consultas</div></div>
          <div class="seg__kpi"><div class="seg__kpinum">{{ conversion }}%</div><div class="seg__kpilbl">Conversión</div></div>
          <div class="seg__kpi"><div class="seg__kpinum">{{ seg.supply }}</div><div class="seg__kpilbl">Propiedades</div></div>
        </div>

        <!-- band heat map -->
        <div class="seg__mapcard">
          <gmap-map v-if="hasPoints" ref="segmap" :center="center" :zoom="12" :options="mapOpts" class="seg__map" />
          <div v-else class="seg__mapempty">Sin datos geográficos en este segmento</div>
        </div>

        <!-- breakdowns -->
        <div class="seg__cols">
          <section class="seg__box">
            <div class="seg__boxlbl">Fuentes de tráfico</div>
            <div v-for="s in sources" :key="s.label" class="seg__row">
              <span class="seg__rowname">{{ sourceLabel(s.label) }}</span>
              <span class="seg__track"><span class="seg__fill" :style="{ width: barW(s.count, maxSource) }"></span></span>
              <span class="seg__rowval">{{ s.count }}</span>
            </div>
          </section>

          <section class="seg__box">
            <div class="seg__boxlbl">Tipo de propiedad</div>
            <div v-if="types.length">
              <div v-for="t in types" :key="t.label" class="seg__row">
                <span class="seg__rowname">{{ typeLabel(t.label) }}</span>
                <span class="seg__track"><span class="seg__fill" :style="{ width: barW(t.count, maxType) }"></span></span>
                <span class="seg__rowval">{{ t.count }}</span>
              </div>
            </div>
            <div v-else class="seg__empty">Sin datos</div>
          </section>
        </div>

        <div class="seg__cols">
          <section class="seg__box">
            <div class="seg__boxlbl">Recurrencia <span class="seg__hint">de {{ seg.uniqueVisitors }} únicos</span></div>
            <div class="seg__row">
              <span class="seg__rowname">Visitó una vez</span>
              <span class="seg__track"><span class="seg__fill seg__fill--soft" :style="{ width: barW(onceVisitors, seg.uniqueVisitors || 1) }"></span></span>
              <span class="seg__rowval">{{ onceVisitors }}</span>
            </div>
            <div class="seg__row">
              <span class="seg__rowname">Recurrentes</span>
              <span class="seg__track"><span class="seg__fill" :style="{ width: barW(seg.recurrentVisitors, seg.uniqueVisitors || 1) }"></span></span>
              <span class="seg__rowval">{{ seg.recurrentVisitors }}</span>
            </div>
            <div class="seg__boxlbl seg__boxlbl--mt">Dispositivo <span class="seg__hint">(parcial)</span></div>
            <div v-for="d in seg.devices" :key="d.label" class="seg__row">
              <span class="seg__rowname">{{ deviceLabel(d.label) }}</span>
              <span class="seg__track"><span class="seg__fill seg__fill--soft" :style="{ width: barW(d.count, maxDevice) }"></span></span>
              <span class="seg__rowval">{{ d.count }}</span>
            </div>
          </section>

          <section class="seg__box">
            <div class="seg__boxlbl">Barrios más buscados</div>
            <div v-if="seg.topBarrios.length">
              <div v-for="(b, i) in seg.topBarrios" :key="i" class="seg__row">
                <span class="seg__rowname">{{ b.neighborhood }} <em>{{ b.city }}</em></span>
                <span class="seg__track"><span class="seg__fill" :style="{ width: barW(b.visits, maxBarrio) }"></span></span>
                <span class="seg__rowval">{{ b.visits }}</span>
              </div>
            </div>
            <div v-else class="seg__empty">Sin barrios con datos</div>
          </section>
        </div>

        <section class="seg__box seg__box--full">
          <div class="seg__boxlbl">Propiedades más vistas</div>
          <div v-if="seg.topListings.length">
            <div v-for="(t, i) in seg.topListings" :key="i" class="seg__listing">
              <span class="seg__rank">{{ i + 1 }}</span>
              <span class="seg__addr">{{ t.property ? t.property.fullAddress : '—' }}</span>
              <span class="seg__rowval">{{ t.count }}</span>
            </div>
          </div>
          <div v-else class="seg__empty">Sin vistas</div>
        </section>

        <div class="seg__foot">
          Demanda por vistas reales en el período. Únicos/recurrentes por cookie de visita; dispositivo según perfil identificado (parcial).
        </div>
      </template>

      <div v-else class="seg__empty seg__empty--pad">Sin demanda registrada en este segmento.</div>
    </div>
  </v-dialog>
</template>

<script>
import { gmapApi } from "vue2-google-maps";
import { BudgetSegment } from "@/graphql/queries/analytics";
import { BROWSER_TZ } from "@/utils/charts";

const SOURCE_LABELS = { search: "Búsqueda", recommendation: "Recomendación", direct: "Directo", landing: "Landing" };
const DEVICE_LABELS = { mobile: "Móvil", desktop: "Escritorio", tablet: "Tablet", desconocido: "Desconocido" };
const TYPE_LABELS = { house: "Casa", apartment: "Departamento", terrain: "Terreno", salon: "Local", farm: "Campo", quinta: "Quinta", country_house: "Casa quinta", parking: "Cochera", warehouse: "Depósito", shed: "Galpón", office: "Oficina" };

export default {
  props: {
    value: { type: Boolean, default: false },
    params: { type: Object, default: () => ({}) }, // { operationType, propertyType, currency, range, min, max }
    title: { type: String, default: "" }
  },

  data: () => ({
    seg: null,
    loading: false,
    center: { lat: -29.15, lng: -59.65 },
    mapOpts: {
      zoomControl: true, mapTypeControl: false, scaleControl: false,
      streetViewControl: false, rotateControl: false, fullscreenControl: true, disableDefaultUi: true
    },
    _circles: [],
    _markers: [],
    _map: null
  }),

  watch: {
    value(open) {
      if (open) this.fetch();
    },
    params: {
      deep: true,
      handler() {
        if (this.value) this.fetch();
      }
    }
  },

  computed: {
    rangeDates() {
      const r = this.params.range || 30;
      const to = new Date();
      const from = new Date();
      from.setDate(to.getDate() - (r - 1));
      return { from: this.dateKey(from), to: this.dateKey(to) };
    },
    sources() {
      return (this.seg && this.seg.sources) || [];
    },
    types() {
      return (this.seg && this.seg.types) || [];
    },
    maxType() { return Math.max(1, ...this.types.map(t => t.count)); },
    hasPoints() {
      return !!(this.seg && this.seg.heatPoints && this.seg.heatPoints.length);
    },
    conversion() {
      if (!this.seg || !this.seg.uniqueVisitors) return 0;
      return Math.round((this.seg.inquiries / this.seg.uniqueVisitors) * 100);
    },
    onceVisitors() {
      if (!this.seg) return 0;
      return Math.max(0, (this.seg.uniqueVisitors || 0) - (this.seg.recurrentVisitors || 0));
    },
    maxSource() { return Math.max(1, ...this.sources.map(s => s.count)); },
    maxDevice() { return Math.max(1, ...((this.seg && this.seg.devices) || []).map(d => d.count)); },
    maxBarrio() { return Math.max(1, ...((this.seg && this.seg.topBarrios) || []).map(b => b.visits)); }
  },

  methods: {
    dateKey(d) {
      const p = n => String(n).padStart(2, "0");
      return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
    },
    sourceLabel(s) { return SOURCE_LABELS[s] || s; },
    deviceLabel(d) { return DEVICE_LABELS[d] || d; },
    typeLabel(t) { return TYPE_LABELS[t] || t; },
    barW(n, max) { return Math.round((n / max) * 100) + "%"; },

    fetch() {
      this.loading = true;
      this.$apollo
        .query({
          query: BudgetSegment,
          fetchPolicy: "no-cache",
          variables: {
            operationType: this.params.operationType,
            propertyType: this.params.propertyType,
            currency: this.params.currency,
            from: this.rangeDates.from,
            to: this.rangeDates.to,
            timeZone: BROWSER_TZ,
            min: this.params.min != null ? this.params.min : null,
            max: this.params.max != null ? this.params.max : null
          }
        })
        .then(res => {
          this.seg = res.data.budgetSegment;
          this.$nextTick(() => this.renderMap());
        })
        .catch(() => { this.seg = null; })
        .finally(() => { this.loading = false; });
    },

    renderMap(attempt = 0) {
      if (!this.hasPoints) return;
      // the gmap-map lives inside a v-dialog — during the open transition $refs.segmap
      // isn't in the DOM yet, so poll until it (and its map promise) exist
      const ref = this.$refs.segmap;
      if (!ref || !ref.$mapPromise) {
        if (attempt < 25) setTimeout(() => this.renderMap(attempt + 1), 150);
        return;
      }
      ref.$mapPromise.then(map => {
        this._map = map;
        this.drawOverlays(map);
        // container can be size 0 mid-transition, so fitBounds lands wrong — redraw once settled
        setTimeout(() => this.drawOverlays(map), 450);
      });
    },

    drawOverlays(map) {
      const g = gmapApi();
      if (!g || !g.maps || !this.seg) return;
      (this._circles || []).forEach(c => c.setMap(null));
      (this._markers || []).forEach(m => m.setMap(null));
      this._circles = [];
      this._markers = [];

      const pts = (this.seg.heatPoints || []).filter(p => p.visits > 0);
      if (!pts.length) return;
      const max = Math.max(...pts.map(p => p.visits));

      pts.forEach(p => {
        const t = p.visits / max;
        // soft density glow (metres) — reads as a heat map when properties cluster
        this._circles.push(new g.maps.Circle({
          map, center: { lat: p.lat, lng: p.lng }, radius: 130 + t * 520,
          strokeWeight: 0, fillColor: "#b52118", fillOpacity: 0.12 + t * 0.33, clickable: false
        }));
        // always-visible marker for the visited property (pixel-scaled, never sub-pixel)
        this._markers.push(new g.maps.Marker({
          map, position: { lat: p.lat, lng: p.lng }, clickable: false,
          icon: { path: g.maps.SymbolPath.CIRCLE, scale: 4 + t * 7, fillColor: "#b52118", fillOpacity: 0.9, strokeColor: "#fff", strokeWeight: 1 }
        }));
      });

      // pan to the heaviest ~11km cluster so the markers/glow aren't lost in a wide view
      const key = p => `${Math.round(p.lat / 0.1)}|${Math.round(p.lng / 0.1)}`;
      const cells = {};
      pts.forEach(p => {
        const id = key(p);
        const c = cells[id] || (cells[id] = { w: 0, lat: 0, lng: 0 });
        c.w += p.visits; c.lat += p.lat * p.visits; c.lng += p.lng * p.visits;
      });
      const top = Object.values(cells).sort((a, b) => b.w - a.w)[0];
      const cLat = top.lat / top.w;
      const cLng = top.lng / top.w;
      const near = pts.filter(p => Math.abs(p.lat - cLat) < 0.06 && Math.abs(p.lng - cLng) < 0.06);

      const b = new g.maps.LatLngBounds();
      (near.length ? near : pts).forEach(p => b.extend({ lat: p.lat, lng: p.lng }));
      g.maps.event.trigger(map, "resize");
      map.fitBounds(b);
      g.maps.event.addListenerOnce(map, "idle", () => { if (map.getZoom() > 16) map.setZoom(16); });
    }
  },

  beforeDestroy() {
    (this._circles || []).forEach(c => c.setMap(null));
    (this._markers || []).forEach(m => m.setMap(null));
  }
};
</script>

<style scoped>
/* solid bg fills the dialog; the v-dialog itself scrolls (no `scrollable` prop, so no
   transparent flash, and no second scrollbar from the panel owning overflow too) */
.seg { background: #f7f5f1; padding: 22px 24px 24px; }
.seg__box--full { margin-bottom: 16px; }
.seg__head { display: flex; align-items: flex-start; justify-content: space-between; gap: 16px; margin-bottom: 18px; }
.seg__eyebrow { font-size: 10.5px; font-weight: 700; letter-spacing: 0.14em; text-transform: uppercase; color: #b52118; margin-bottom: 5px; }
.seg__title { font-size: 20px; font-weight: 700; color: #1a1814; margin: 0; }
.seg__close { border: none; background: transparent; font-size: 26px; line-height: 1; color: #a39a8c; cursor: pointer; }
.seg__loading, .seg__empty--pad { padding: 40px 0; text-align: center; color: #a39a8c; }

.seg__kpis { display: grid; grid-template-columns: repeat(6, 1fr); gap: 10px; margin-bottom: 16px; }
@media (max-width: 700px) { .seg__kpis { grid-template-columns: repeat(3, 1fr); } }
.seg__kpi { background: #fff; border: 1px solid #ece7de; border-radius: 11px; padding: 12px 13px; }
.seg__kpinum { font-size: 22px; font-weight: 800; color: #1a1814; line-height: 1; }
.seg__kpilbl { font-size: 10.5px; color: #756f64; margin-top: 5px; }

.seg__mapcard { position: relative; background: #fff; border: 1px solid #ece7de; border-radius: 14px; overflow: hidden; height: 300px; margin-bottom: 16px; }
.seg__map { position: absolute; top: 0; right: 0; bottom: 0; left: 0; width: 100%; height: 100%; }
.seg__mapempty { display: flex; align-items: center; justify-content: center; height: 100%; color: #a39a8c; font-size: 13px; }

.seg__cols { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; margin-bottom: 16px; }
@media (max-width: 700px) { .seg__cols { grid-template-columns: 1fr; } }
.seg__box { background: #fff; border: 1px solid #ece7de; border-radius: 14px; padding: 15px 17px; }
.seg__boxlbl { font-size: 10px; font-weight: 700; letter-spacing: 0.14em; text-transform: uppercase; color: #a39a8c; margin-bottom: 11px; }
.seg__boxlbl--mt { margin-top: 16px; }
.seg__hint { color: #c2b9aa; font-weight: 600; letter-spacing: 0; text-transform: none; }

.seg__row { display: grid; grid-template-columns: 1fr 110px 38px; align-items: center; gap: 10px; margin-bottom: 8px; }
.seg__rowname { font-size: 12.5px; color: #1a1814; font-weight: 600; overflow: hidden; white-space: nowrap; text-overflow: ellipsis; }
.seg__rowname em { font-style: normal; color: #a39a8c; font-weight: 500; font-size: 11px; margin-left: 5px; }
.seg__track { height: 7px; background: #f1ece3; border-radius: 4px; overflow: hidden; }
.seg__fill { display: block; height: 100%; background: #b52118; border-radius: 4px; }
.seg__fill--alt { background: #c89b3c; }
.seg__fill--soft { background: #a39a8c; }
.seg__rowval { font-size: 12.5px; font-weight: 700; color: #1a1814; text-align: right; }

.seg__listing { display: grid; grid-template-columns: 18px 1fr auto; align-items: center; gap: 10px; margin-bottom: 8px; }
.seg__rank { font-size: 11px; font-weight: 700; color: #c2b9aa; text-align: center; }
.seg__addr { font-size: 12.5px; color: #1a1814; overflow: hidden; white-space: nowrap; text-overflow: ellipsis; }

.seg__empty { color: #a39a8c; font-size: 12.5px; }
.seg__foot { font-size: 11px; color: #a39a8c; }
</style>
